"use client";
import { FC, useRef, useCallback, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Grid, Navigation } from "swiper/modules";

import GameCard from "./GameCard";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { I18N_NAMESPACE } from "@/config/i18n";
import SVGIcon from "../SVGIcon";
import { usePathname } from "../../navigation";
import { useDispatch } from "react-redux";
import useTracking from "../Hooks/useTracking";
import { setGameCardTooltip } from "../../fe-core-omega/_redux/actions/casinoActions";
import { ICasinoGroupViewModel } from "../../fe-core-omega/meta/interfaces/casino";
import { isMobile } from "react-device-detect";
const CasinoGroupWidgetSwiper: FC<{
  group: ICasinoGroupViewModel;
  title: String;
  icon?: string;
  total?: number;
  href?: string;
  hidePlayButton?: boolean;
}> = ({ group, title, icon, total, href, hidePlayButton = true }) => {
  const pathname = usePathname() ?? "";
  const { navigate, click } = useTracking();
  const dispatch = useDispatch();
  const t = useTranslations(I18N_NAMESPACE.GENERAL);

  useEffect(() => {
    dispatch(setGameCardTooltip(null));
  }, [pathname]);
  const games = group?.games?.items?.slice(0, 20);

  const sliderRef = useRef<any>(null);
  const navigationPrevRef = useRef<any>(null);
  const navigationNextRef = useRef<any>(null);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    click(`Previous Arrow Clicked`, {
      selectedCategory: group?.name,
      component: "CasinoSlider - CasinoGroupWidgetSwiper",
    });
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    click(`Next Arrow Clicked`, {
      selectedCategory: group?.name,
      component: "CasinoSlider - CasinoGroupWidgetSwiper",
    });
    sliderRef.current.swiper.slideNext();
  }, []);
  return (
    <>
      {(group.name || title) && (
        <div className="flex justify-between mb-3 sm:mb-4 border-b border-tertiary-300 pb-3">
          <div className="flex items-center gap-2 ">
            {/*<Icon name={icon} size="small" color="grey-1" />*/}
            <div className="text-sm font-bold md:text-xl text-primary capitalize">
              {group.name ? group.name : title}
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <Link
              href={href || `/casino/${group?.slug}`}
              // scroll={false}
              passHref
              className="text-[#868686] font-semibold text-xs flex items-center gap-2 hover:text-primary-500 group transition-all"
              onClick={() => {
                navigate(`${href || `/casino/${group?.slug}`}`, {
                  component: "casinoSlider - CasinoGroupWidgetSwiper",
                  buttonLabel: t("view-all"),
                });
              }}
            >
              <span>{t("view-all")}</span>
              {total && (
                <span className="hidden md:block px-3 py-1 rounded-full bg-[#868686] text-tertiary group-hover:bg-primary transition-all">
                  {total}
                </span>
              )}
            </Link>
            <div className="lg:flex gap-0.5 hidden">
              <button
                ref={navigationPrevRef}
                className="btn btn--white w-7 h-7 !p-0 sm:btn--square"
                onClick={handlePrev}
              >
                <SVGIcon
                  icon={"angleArrow"}
                  className="w-3 h-3 text-tertiary/70 dark:text-white/70 -ml-0.5 block transform rotate-180 "
                />
              </button>
              <button
                ref={navigationNextRef}
                className="btn btn--white w-7 h-7 !p-0 sm:btn--square"
                onClick={handleNext}
              >
                <SVGIcon
                  icon={"angleArrow"}
                  className="block w-3 h-3 text-tertiary/70 dark:text-white/70 -mr-0.5  "
                />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="relative w-full" data-row={group?.numberOfRows}>
        <Swiper
          ref={sliderRef}
          spaceBetween={isMobile ? 4 : 12}
          slidesPerView={group?.numberOfGamesPerRow || 7}
          freeMode={isMobile ? true : false}
          navigation={{
            prevEl: navigationPrevRef?.current,
            nextEl: navigationNextRef?.current,
          }}
          grid={{
            rows: group?.numberOfRows || 1,
            fill: "row",
          }}
          modules={[Grid, Navigation]}
          breakpoints={{
            0: {
              slidesPerView: 3.3,
              spaceBetween: 4,
            },
            768: {
              slidesPerView: 3.3,
              spaceBetween: 4,
            },
            900: {
              slidesPerView: 4,
            },
            1100: {
              slidesPerView: 5,
            },
            1280: {
              slidesPerView: group?.numberOfGamesPerRow || 7,
            },
          }}
        >
          {games?.map((game, i) => (
            <SwiperSlide key={game?.id}>
              <GameCard
                game={games[i]}
                key={`${group?.id}-game-${i}`}
                hidePlayButton={hidePlayButton}
                showFeaturedThumbnail={group?.showFeaturedThumbnail}
                featuredThumbnailWidth={group?.featuredThumbnailWidth}
                featuredThumbnailHeight={group?.featuredThumbnailHeight}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  );
};

export default CasinoGroupWidgetSwiper;
